/**
 * AImry Avatar - Lazy Loading Wrapper
 *
 * Provides a lazy-loaded version of the AImry static avatar.
 */

import React, { Suspense, lazy, ComponentType } from 'react';
import { cn } from '@/lib/utils';

interface AvatarProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Loading fallback shown while avatar loads
 */
const AImryAvatarLoadingFallback: React.FC<AvatarProps> = ({
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-28 h-28',
    xl: 'w-36 h-36',
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full overflow-hidden',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: 'var(--theme-card-yellow, #fef08a)',
      }}
    >
      <div className="animate-pulse flex flex-col items-center gap-1">
        <span className="text-2xl">🤖</span>
      </div>
    </div>
  );
};

const LazyAImryStaticAvatar = lazy(() =>
  import('./AImry3DAvatar').then(module => ({
    default: module.AImryStaticAvatar as ComponentType<AvatarProps>,
  }))
);

export const AImryStaticAvatarLazy: React.FC<AvatarProps> = (props) => {
  return (
    <Suspense fallback={<AImryAvatarLoadingFallback size={props.size} className={props.className} />}>
      <LazyAImryStaticAvatar {...props} />
    </Suspense>
  );
};

export default AImryStaticAvatarLazy;
